<template>
  <div class="dashboard-container">

    <!-- ── Encabezado ─────────────────────────────────────────────────── -->
    <div class="dashboard-header mb-4">
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <h1 class="dashboard-title mb-1">Dashboard</h1>
          <p class="dashboard-subtitle mb-0">
            Bienvenido, <strong>{{ nombreEmpresa }}</strong> &mdash; {{ fechaActual }}
          </p>
        </div>
        <button class="btn btn-light btn-sm" @click="cargarDatos" :disabled="cargando">
          <i class="bi" :class="cargando ? 'bi-hourglass-split' : 'bi-arrow-clockwise'"></i>
          {{ cargando ? 'Actualizando…' : 'Actualizar' }}
        </button>
      </div>
    </div>

    <!-- ── Spinner de carga inicial ───────────────────────────────────── -->
    <div v-if="cargando && !datos" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando…</span>
      </div>
      <p class="text-muted mt-3">Cargando datos del dashboard…</p>
    </div>

    <!-- ── Error de carga ─────────────────────────────────────────────── -->
    <div v-if="errorCarga" class="alert alert-danger d-flex align-items-start gap-2">
      <i class="bi bi-exclamation-triangle-fill flex-shrink-0 mt-1"></i>
      <div class="flex-grow-1">
        <div><strong>Error al cargar dashboard</strong></div>
        <small class="d-block">{{ errorCarga }}</small>
      </div>
      <button class="btn btn-sm btn-outline-danger ms-auto flex-shrink-0" @click="cargarDatos">Reintentar</button>
    </div>

    <!-- ── Accesos rapidos (usuarios no administradores) ──────────────── -->
    <div v-if="datos && !puedeVerResumen" class="row g-3 mb-4">
      <div class="col-12">
        <h5 class="mb-3"><i class="bi bi-grid-3x3-gap me-2 text-primary"></i>Accesos Rápidos</h5>
      </div>
      <div v-if="accesosRapidos.length === 0" class="col-12">
        <div class="alert alert-secondary">No tienes módulos habilitados todavía. Contacta al administrador.</div>
      </div>
      <div v-for="acceso in accesosRapidos" :key="acceso.ruta" class="col-xl-3 col-lg-4 col-sm-6">
        <router-link :to="acceso.ruta" class="acceso-rapido-card">
          <div class="acceso-rapido-icono"><i :class="acceso.icono"></i></div>
          <div class="acceso-rapido-label">{{ acceso.label }}</div>
        </router-link>
      </div>
    </div>

    <template v-if="datos && puedeVerResumen">

      <!-- ── 1. KPI Cards ────────────────────────────────────────────── -->
      <div class="row g-3 mb-4">

        <!-- Ventas del mes -->
        <div class="col-xl-3 col-lg-6 col-sm-6">
          <div class="kpi-card kpi-primary">
            <div class="kpi-icon"><i class="bi bi-cash-coin"></i></div>
            <div class="kpi-body">
              <div class="kpi-valor">{{ formatDinero(datos.kpis.ventas_mes) }}</div>
              <div class="kpi-etiqueta">Ventas del Mes (sin IVA)</div>
              <div class="kpi-sub">
                IVA: {{ formatDinero(datos.kpis.iva_ventas_mes) }} ·
                {{ datos.kpis.facturas_autorizadas }} factura(s)
              </div>
            </div>
          </div>
        </div>

        <!-- Ganancia aproximada -->
        <div class="col-xl-3 col-lg-6 col-sm-6">
          <div :class="['kpi-card', datos.kpis.ganancia_aproximada >= 0 ? 'kpi-success' : 'kpi-danger']">
            <div class="kpi-icon"><i class="bi bi-graph-up-arrow"></i></div>
            <div class="kpi-body">
              <div class="kpi-valor">{{ formatDinero(datos.kpis.ganancia_aproximada) }}</div>
              <div class="kpi-etiqueta">Ganancia Aproximada</div>
              <div class="kpi-sub">Ventas sin IVA - compras sin IVA</div>
            </div>
          </div>
        </div>

        <!-- Compras del mes -->
        <div class="col-xl-3 col-lg-6 col-sm-6">
          <div class="kpi-card kpi-danger">
            <div class="kpi-icon"><i class="bi bi-cart-dash-fill"></i></div>
            <div class="kpi-body">
              <div class="kpi-valor">{{ formatDinero(datos.kpis.compras_mes_monto) }}</div>
              <div class="kpi-etiqueta">Compras del Mes (sin IVA)</div>
              <div class="kpi-sub">
                IVA: {{ formatDinero(datos.kpis.iva_compras_mes) }} ·
                {{ datos.kpis.compras_mes_count }} compra(s)
              </div>
            </div>
          </div>
        </div>

        <!-- Facturas generadas del mes -->
        <div class="col-xl-3 col-lg-6 col-sm-6">
          <div class="kpi-card kpi-info">
            <div class="kpi-icon"><i class="bi bi-receipt"></i></div>
            <div class="kpi-body">
              <div class="kpi-valor">{{ datos.kpis.facturas_generadas }}</div>
              <div class="kpi-etiqueta">Facturas del Mes</div>
              <div class="kpi-sub">{{ datos.kpis.facturas_autorizadas }} autorizada(s)</div>
            </div>
          </div>
        </div>

      </div>

      <!-- ── 2. Gráfico ventas anuales ─────────────────────────────── -->
      <div class="row g-3 mb-4">
        <div class="col-12">
          <div class="panel-card">
            <div class="panel-header">
              <h5 class="panel-titulo">
                <i class="bi bi-bar-chart-line me-2 text-primary"></i>
                Subtotal de Ventas Anuales (sin IVA)
              </h5>
            </div>
            <div class="panel-body">
              <div class="grafico-contenedor">
                <canvas ref="canvasVentas" height="220"></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── 3. Cartera por cobrar + Cartera por pagar ──────────────── -->
      <div class="row g-3 mb-4">

        <!-- Cartera por cobrar -->
        <div class="col-xl-6">
          <div class="panel-card h-100">
            <div class="panel-header">
              <h5 class="panel-titulo">
                <i class="bi bi-clock-history me-2 text-warning"></i>
                Deudas clientes
              </h5>
              <div class="cartera-header-total">
                <span class="cartera-header-label">Total</span>
                <span class="cartera-header-valor">{{ formatDinero(datos.cartera.total) }}</span>
              </div>
            </div>
            <div class="panel-body">
              <div class="cartera-lista">
                <div class="cartera-fila" v-if="datos.cartera.vencido > 0">
                  <span class="cartera-tramo tramo-vencido-total">Vencido</span>
                  <span class="cartera-monto cartera-monto-vencido">{{ formatDinero(datos.cartera.vencido) }}</span>
                </div>
                <div class="cartera-fila">
                  <span class="cartera-tramo tramo-30">0 – 30 días</span>
                  <span class="cartera-monto">{{ formatDinero(datos.cartera.dias_30) }}</span>
                </div>
                <div class="cartera-fila">
                  <span class="cartera-tramo tramo-60">31 – 60 días</span>
                  <span class="cartera-monto">{{ formatDinero(datos.cartera.dias_60) }}</span>
                </div>
                <div class="cartera-fila">
                  <span class="cartera-tramo tramo-90">61 – 90 días</span>
                  <span class="cartera-monto">{{ formatDinero(datos.cartera.dias_90) }}</span>
                </div>
                <div class="cartera-fila">
                  <span class="cartera-tramo tramo-120">91 – 120 días</span>
                  <span class="cartera-monto">{{ formatDinero(datos.cartera.dias_120) }}</span>
                </div>
                <div class="cartera-fila">
                  <span class="cartera-tramo tramo-vencido">+ 120 días</span>
                  <span class="cartera-monto">{{ formatDinero(datos.cartera.mas_120) }}</span>
                </div>
              </div>
              <p v-if="datos.cartera.total === 0" class="text-muted text-center mt-3 mb-0">
                <i class="bi bi-check-circle-fill text-success me-1"></i>Sin cartera pendiente
              </p>
            </div>
          </div>
        </div>

        <!-- Cartera por pagar -->
        <div class="col-xl-6">
          <div class="panel-card h-100">
            <div class="panel-header">
              <h5 class="panel-titulo">
                <i class="bi bi-arrow-up-circle me-2 cartera-pagar-icon"></i>
                Deudas proveedores
              </h5>
              <div class="cartera-header-total cartera-pagar-total">
                <span class="cartera-header-label">Total</span>
                <span class="cartera-header-valor">{{ formatDinero(datos.cartera_pagar.total) }}</span>
              </div>
            </div>
            <div class="panel-body">
              <div class="cartera-lista">
                <div class="cartera-fila" v-if="datos.cartera_pagar.vencido > 0">
                  <span class="cartera-tramo tramo-vencido-total">Vencido</span>
                  <span class="cartera-monto cartera-monto-vencido">{{ formatDinero(datos.cartera_pagar.vencido) }}</span>
                </div>
                <div class="cartera-fila">
                  <span class="cartera-tramo tramo-30">0 – 30 días</span>
                  <span class="cartera-monto">{{ formatDinero(datos.cartera_pagar.dias_30) }}</span>
                </div>
                <div class="cartera-fila">
                  <span class="cartera-tramo tramo-60">31 – 60 días</span>
                  <span class="cartera-monto">{{ formatDinero(datos.cartera_pagar.dias_60) }}</span>
                </div>
                <div class="cartera-fila">
                  <span class="cartera-tramo tramo-90">61 – 90 días</span>
                  <span class="cartera-monto">{{ formatDinero(datos.cartera_pagar.dias_90) }}</span>
                </div>
                <div class="cartera-fila">
                  <span class="cartera-tramo tramo-120">91 – 120 días</span>
                  <span class="cartera-monto">{{ formatDinero(datos.cartera_pagar.dias_120) }}</span>
                </div>
                <div class="cartera-fila">
                  <span class="cartera-tramo tramo-vencido">+ 120 días</span>
                  <span class="cartera-monto">{{ formatDinero(datos.cartera_pagar.mas_120) }}</span>
                </div>
              </div>
              <p v-if="datos.cartera_pagar.total === 0" class="text-muted text-center mt-3 mb-0">
                <i class="bi bi-check-circle-fill text-success me-1"></i>Sin deudas pendientes
              </p>
            </div>
          </div>
        </div>

      </div>

      <!-- ── 4. Top Productos + Top Clientes del mes ───────────────── -->
      <div class="row g-3">

        <!-- Top 10 productos más vendidos -->
        <div class="col-xl-6">
          <div class="panel-card">
            <div class="panel-header">
              <h5 class="panel-titulo">
                <i class="bi bi-trophy me-2 text-warning"></i>
                Productos del Mes
              </h5>
            </div>
            <div class="panel-body">

              <div v-if="datos.top_productos && datos.top_productos.length > 0">
                <div
                  class="top-item"
                  v-for="(prod, index) in datos.top_productos"
                  :key="prod.id"
                >
                  <div class="top-rango">{{ index + 1 }}</div>
                  <div class="top-info flex-grow-1">
                    <div class="top-nombre">{{ prod.nombre }}</div>
                    <small class="text-muted">{{ prod.codigo || 'Sin código' }}</small>
                    <div class="progress mt-1" style="height:4px;">
                      <div
                        class="progress-bar bg-warning"
                        :style="{ width: prod.porcentaje + '%' }"
                      ></div>
                    </div>
                  </div>
                  <div class="top-monto text-end">
                    <div class="fw-bold">{{ formatCantidad(prod.unidades_vendidas) }} uds.</div>
                    <small class="text-muted">{{ formatDinero(prod.monto_total) }}</small>
                  </div>
                </div>
              </div>

              <div v-else class="text-center text-muted py-4">
                <i class="bi bi-box-seam fs-3 d-block mb-2"></i>
                No existen datos registrados.
              </div>

            </div>
          </div>
        </div>

        <!-- Top 10 clientes del mes -->
        <div class="col-xl-6">
          <div class="panel-card">
            <div class="panel-header">
              <h5 class="panel-titulo">
                <i class="bi bi-people me-2 text-info"></i>
                Clientes del Mes
              </h5>
            </div>
            <div class="panel-body">

              <div v-if="datos.top_clientes && datos.top_clientes.length > 0">
                <div
                  class="top-item"
                  v-for="(cli, index) in datos.top_clientes"
                  :key="cli.id"
                >
                  <div class="top-rango top-rango-info">{{ index + 1 }}</div>
                  <div class="top-info flex-grow-1">
                    <div class="top-nombre">{{ cli.nombre }}</div>
                    <small class="text-muted">{{ cli.identificacion }}</small>
                    <div class="progress mt-1" style="height:4px;">
                      <div
                        class="progress-bar bg-info"
                        :style="{ width: cli.porcentaje + '%' }"
                      ></div>
                    </div>
                  </div>
                  <div class="top-monto text-end">
                    <div class="fw-bold">{{ formatDinero(cli.subtotal_sin_iva) }}</div>
                    <small class="text-muted">{{ cli.num_facturas }} factura(s)</small>
                  </div>
                </div>
              </div>

              <div v-else class="text-center text-muted py-4">
                <i class="bi bi-person-x fs-3 d-block mb-2"></i>
                No existen datos registrados.
              </div>

            </div>
          </div>
        </div>

      </div>

    </template>

  </div>
</template>

<script>
import { markRaw } from 'vue'
import api from '@/services/api.js'
import themeManager from '@/store/theme.js'
import { mapGetters } from 'vuex'

export default {
  name: 'ClientDashboard',

  data() {
    return {
      cargando: false,
      errorCarga: null,
      datos: null,
      puedeVerResumen: false,
      graficoInstancia: null,
      graficoInicializado: false,
      destruyendo: false,
      // Mapa manual porque `modulo.icono` no esta poblado para casi ningun
      // modulo en la base; cada entrada es ruta-de-submodulo -> icono bootstrap.
      iconosAccesoRapido: {
        'ventas/facturacion':    { icono: 'bi bi-receipt',          label: 'Facturación' },
        'ventas/pedidos':        { icono: 'bi bi-bag-check',        label: 'Pedidos' },
        'taller/ordenes':        { icono: 'bi bi-tools',            label: 'Taller' },
        'proformas':             { icono: 'bi bi-file-text',        label: 'Proformas' },
        'clientes':              { icono: 'bi bi-people',           label: 'Clientes' },
        'inventario/productos':  { icono: 'bi bi-box-seam',         label: 'Productos' },
        'inventario/stock':      { icono: 'bi bi-boxes',            label: 'Stock' },
        'compras/ordenes':       { icono: 'bi bi-cart',             label: 'Compras' },
        'cajas':                 { icono: 'bi bi-cash-register',    label: 'Caja' },
        'reportes/ventas':       { icono: 'bi bi-bar-chart',        label: 'Reportes' },
        'configuracion/sri':     { icono: 'bi bi-gear',             label: 'Configuración' },
        'contabilidad/asientos': { icono: 'bi bi-journal-text',     label: 'Contabilidad' },
      },
    }
  },

  computed: {
    ...mapGetters(['currentUser', 'activeModules']),

    accesosRapidos() {
      const permitidas = new Set((this.activeModules || []).map(String))
      return Object.entries(this.iconosAccesoRapido)
        .filter(([ruta]) => permitidas.has('/' + ruta))
        .map(([ruta, info]) => ({ ruta: '/' + ruta, ...info }))
    },

    nombreEmpresa() {
      const c = this.currentUser
      if (!c) return 'su empresa'
      return (
        c.company?.business_name ||
        c.company?.razon_social  ||
        c.company?.nombre_comercial ||
        c.email ||
        'su empresa'
      )
    },

    fechaActual() {
      return new Date().toLocaleDateString('es-EC', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
        timeZone: 'America/Guayaquil',
      })
    },
  },

  async mounted() {
    themeManager.init()
    await this.cargarDatos()
  },

  beforeUnmount() {
    this.destruyendo = true
    this.destruirGrafico()
  },

  watch: {
    'themeManager.isDarkMode'(nuevo, anterior) {
      if (nuevo !== anterior && !this.destruyendo && this.datos) {
        this.$nextTick(() => this.renderizarGrafico())
      }
    },
  },

  methods: {
    // ─── Carga de datos desde el backend ─────────────────────────────
    async cargarDatos() {
      this.cargando = true
      this.errorCarga = null

      try {
        const respuesta = await api.get('/dashboard/resumen')

        if (respuesta.data?.exito) {
          const d = respuesta.data.datos || {}
          this.puedeVerResumen = !!d.puede_ver_resumen
          this.datos = {
            periodo:        d.periodo        || { anio: new Date().getFullYear(), anio_anterior: new Date().getFullYear() - 1, mes: new Date().getMonth() + 1, label: '' },
            kpis:           d.kpis           || { ventas_mes: 0, iva_ventas_mes: 0, facturas_autorizadas: 0, ganancia_aproximada: 0, compras_mes_count: 0, compras_mes_monto: 0, iva_compras_mes: 0, facturas_generadas: 0 },
            ventas_anuales: d.ventas_anuales || { meses: [], anio_actual: [], anio_anterior: [], iva_anio_actual: [], iva_anio_anterior: [] },
            cartera:        d.cartera        || { vencido: 0, dias_30: 0, dias_60: 0, dias_90: 0, dias_120: 0, mas_120: 0, total: 0 },
            cartera_pagar:  d.cartera_pagar  || { vencido: 0, dias_30: 0, dias_60: 0, dias_90: 0, dias_120: 0, mas_120: 0, total: 0 },
            top_productos:  d.top_productos  || [],
            top_clientes:   d.top_clientes   || [],
          }
          if (this.puedeVerResumen) {
            await this.$nextTick()
            this.renderizarGrafico()
          }
        } else {
          this.errorCarga = respuesta.data?.error || 'No se pudo obtener el resumen del dashboard.'
        }
      } catch (err) {
        console.error('[Dashboard] Error al cargar datos:', err)
        const serverMsg = err.response?.data?.detalle || err.response?.data?.error || null
        this.errorCarga = serverMsg
          ? `Error del servidor: ${serverMsg}`
          : `Error ${err.response?.status || ''}: ${err.message}`
      } finally {
        this.cargando = false
      }
    },

    // ─── Gráfico de barras — ventas por día ──────────────────────────
    async renderizarGrafico() {
      if (this.destruyendo) return

      try {
        const { default: Chart } = await import('chart.js/auto')
        this.crearGrafico(Chart)
      } catch (err) {
        console.warn('[Dashboard] Chart.js no disponible:', err.message)
      }
    },

    crearGrafico(Chart) {
      const canvas = this.$refs.canvasVentas
      if (!canvas || !canvas.isConnected) return

      this.destruirGrafico()

      const ventasAnuales  = this.datos?.ventas_anuales ?? {}
      const etiquetas      = ventasAnuales.meses ?? []
      const valoresActual  = ventasAnuales.anio_actual ?? []
      const valoresAnterior = ventasAnuales.anio_anterior ?? []
      const anioActual     = this.datos?.periodo?.anio ?? new Date().getFullYear()
      const anioAnterior   = this.datos?.periodo?.anio_anterior ?? (anioActual - 1)

      const esModoOscuro = document.documentElement.classList.contains('dark-theme') ||
                           document.body.classList.contains('dark-theme') ||
                           window.matchMedia('(prefers-color-scheme: dark)').matches

      const colorTexto = esModoOscuro ? '#cbd5e0' : '#4a5568'
      const colorGrid  = esModoOscuro ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'

      this.graficoInstancia = markRaw(new Chart(canvas, {
        type: 'line',
        data: {
          labels: etiquetas,
          datasets: [
            {
              label: String(anioActual),
              data: valoresActual,
              borderColor: '#667eea',
              backgroundColor: 'rgba(102,126,234,0.12)',
              borderWidth: 2.5,
              pointBackgroundColor: '#667eea',
              pointRadius: 4,
              pointHoverRadius: 6,
              fill: true,
              tension: 0.4,
              anioCorto: String(anioActual).slice(2),
            },
            {
              label: String(anioAnterior),
              data: valoresAnterior,
              borderColor: 'rgba(160,174,192,0.8)',
              backgroundColor: 'rgba(160,174,192,0.07)',
              borderWidth: 2,
              pointBackgroundColor: 'rgba(160,174,192,0.8)',
              pointRadius: 3,
              pointHoverRadius: 5,
              fill: true,
              tension: 0.4,
              borderDash: [5, 4],
              anioCorto: String(anioAnterior).slice(2),
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                color: colorTexto,
                font: { size: 12 },
                generateLabels: (chart) => chart.data.datasets.map((ds, i) => ({
                  text: ds.label,
                  fillStyle: ds.borderColor,
                  strokeStyle: ds.borderColor,
                  fontColor: colorTexto,
                  lineWidth: 2,
                  hidden: !chart.isDatasetVisible(i),
                  datasetIndex: i,
                })),
              },
            },
            tooltip: {
              callbacks: {
                title: (items) => {
                  const ctx = items[0]
                  return ctx.label
                },
                label: (ctx) => {
                  const anioCorto = ctx.dataset.anioCorto ?? String(ctx.dataset.label).slice(2)
                  const mes = ctx.label
                  return ` ${mes}-${anioCorto}: $${parseFloat(ctx.parsed.y).toFixed(2)}`
                },
              },
            },
          },
          scales: {
            x: {
              ticks: { color: colorTexto },
              grid: { color: colorGrid },
            },
            y: {
              ticks: {
                color: colorTexto,
                callback: (v) => '$' + v.toLocaleString('es-EC'),
              },
              grid: { color: colorGrid },
              beginAtZero: true,
            },
          },
        },
      }))

      this.graficoInicializado = true
    },

    destruirGrafico() {
      if (this.graficoInstancia) {
        try { this.graficoInstancia.destroy() } catch { /* ignorar */ }
        this.graficoInstancia = null
        this.graficoInicializado = false
      }
    },

    // ─── Utilidades de formato ────────────────────────────────────────
    formatDinero(valor) {
      const num = parseFloat(valor) || 0
      return '$ ' + num.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    formatCantidad(valor) {
      const num = parseFloat(valor) || 0
      return num % 1 === 0 ? num.toString() : num.toFixed(2)
    },
  },
}
</script>

<style scoped>
/* ── Contenedor general ─────────────────────────────────────────────── */
.dashboard-container {
  padding: 20px;
  min-height: 100vh;
}

/* ── Encabezado ─────────────────────────────────────────────────────── */
.dashboard-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 28px 32px;
  border-radius: 16px;
  color: white;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
}

.dashboard-subtitle {
  font-size: 1rem;
  opacity: 0.9;
}

/* ── KPI Cards ──────────────────────────────────────────────────────── */
.kpi-card {
  border-radius: 14px;
  padding: 22px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  background: #fff;
  transition: transform 0.2s, box-shadow 0.2s;
  border-left: 5px solid transparent;
}

.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14);
}

.kpi-primary { border-left-color: #667eea; }
.kpi-success { border-left-color: #48bb78; }
.kpi-info    { border-left-color: #4299e1; }
.kpi-warning { border-left-color: #ed8936; }
.kpi-danger  { border-left-color: #f56565; }

.dark-theme .kpi-card {
  background: #1e2535;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

.kpi-icon {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  flex-shrink: 0;
  color: white;
}

.kpi-primary .kpi-icon { background: linear-gradient(135deg, #667eea, #764ba2); }
.kpi-success .kpi-icon { background: linear-gradient(135deg, #48bb78, #38a169); }
.kpi-info    .kpi-icon { background: linear-gradient(135deg, #4299e1, #3182ce); }
.kpi-warning .kpi-icon { background: linear-gradient(135deg, #ed8936, #dd6b20); }
.kpi-danger  .kpi-icon { background: linear-gradient(135deg, #f56565, #e53e3e); }

.kpi-body {
  min-width: 0;
}

.kpi-valor {
  font-size: 1.55rem;
  font-weight: 700;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kpi-etiqueta {
  font-size: 0.85rem;
  color: #718096;
  margin-top: 3px;
}

.dark-theme .kpi-etiqueta { color: #a0aec0; }

.kpi-sub {
  font-size: 0.78rem;
  color: #a0aec0;
  margin-top: 2px;
}

/* ── Panel genérico ─────────────────────────────────────────────────── */
.panel-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.dark-theme .panel-card {
  background: #1e2535;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

.panel-header {
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dark-theme .panel-header {
  background: linear-gradient(135deg, #2d3748, #1a202c);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.panel-titulo {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.panel-body {
  padding: 20px;
}

/* ── Gráfico ────────────────────────────────────────────────────────── */
.grafico-contenedor {
  position: relative;
  height: 220px;
  width: 100%;
}

/* ── Estados del mes ────────────────────────────────────────────────── */
.estado-lista {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.estado-fila {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.estado-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.badge-estado {
  font-size: 0.78rem;
  padding: 3px 10px;
  border-radius: 20px;
  font-weight: 600;
}

.badge-success   { background: #c6f6d5; color: #276749; }
.badge-danger    { background: #fed7d7; color: #9b2c2c; }
.badge-warning   { background: #fefcbf; color: #744210; }
.badge-info      { background: #bee3f8; color: #2a4365; }
.badge-primary   { background: #e9d8fd; color: #553c9a; }
.badge-secondary { background: #e2e8f0; color: #4a5568; }
.badge-dark      { background: #cbd5e0; color: #1a202c; }

.estado-valor {
  font-weight: 700;
  font-size: 0.95rem;
}

/* ── Actividad de hoy ───────────────────────────────────────────────── */
.actividad-circulo {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  font-size: 1.8rem;
  font-weight: 700;
  color: white;
}

.actividad-total { background: linear-gradient(135deg, #667eea, #764ba2); }
.actividad-ok    { background: linear-gradient(135deg, #48bb78, #38a169); }
.actividad-error { background: linear-gradient(135deg, #f56565, #e53e3e); }

.actividad-numero {
  line-height: 1;
}

/* ── Salud del negocio ──────────────────────────────────────────────── */
.salud-metrica {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 10px;
  padding: 12px 8px;
  text-align: center;
}

.dark-theme .salud-metrica {
  background: rgba(255, 255, 255, 0.05);
}

.salud-num {
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1.1;
}

/* ── Top productos / clientes ───────────────────────────────────────── */
.top-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.dark-theme .top-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.top-item:last-child {
  border-bottom: none;
}

.top-rango {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ed8936, #dd6b20);
  color: white;
  font-weight: 700;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.top-rango-info {
  background: linear-gradient(135deg, #4299e1, #3182ce);
}

.top-nombre {
  font-weight: 600;
  font-size: 0.9rem;
  line-height: 1.2;
}

.top-monto {
  flex-shrink: 0;
  font-size: 0.9rem;
}

/* ── Modo oscuro: ajuste de textos ──────────────────────────────────── */
.dark-theme .text-muted {
  color: #a0aec0 !important;
}

/* ── Animación de entrada ────────────────────────────────────────────── */
@keyframes subirFadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}

.kpi-card,
.panel-card {
  animation: subirFadeIn 0.45s ease-out both;
}

/* ── Accesos rapidos ─────────────────────────────────────────────────── */
.acceso-rapido-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 28px 16px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s, box-shadow 0.2s;
  height: 100%;
}
.acceso-rapido-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  color: inherit;
}
.dark-theme .acceso-rapido-card {
  background: #1e2535;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}
.acceso-rapido-icono {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  color: white;
  background: linear-gradient(135deg, #667eea, #764ba2);
}
.acceso-rapido-label {
  font-weight: 600;
  text-align: center;
}

/* ── Responsive ─────────────────────────────────────────────────────── */
@media (max-width: 576px) {
  .dashboard-title { font-size: 1.5rem; }
  .kpi-valor       { font-size: 1.2rem; }
  .panel-body      { padding: 14px; }
  .dashboard-header { padding: 20px; }
}

/* ── Cartera por cobrar ──────────────────────────────────────────────── */

/* Total en el header del panel */
.cartera-header-total {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}
.cartera-header-label {
  font-size: 0.72rem;
  font-weight: 500;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.dark-theme .cartera-header-label {
  color: #a0aec0;
}
.cartera-header-valor {
  font-size: 1.2rem;
  font-weight: 700;
  color: #ed8936;
  line-height: 1.1;
}
.dark-theme .cartera-header-valor {
  color: #f6ad55;
}

/* Lista de tramos */
.cartera-lista {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.cartera-fila {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.cartera-tramo {
  font-size: 0.78rem;
  padding: 3px 12px;
  border-radius: 20px;
  font-weight: 600;
  white-space: nowrap;
}
.tramo-ok      { background: #c6f6d5; color: #276749; }
.tramo-30      { background: #fefcbf; color: #744210; }
.tramo-60      { background: #feebc8; color: #7b341e; }
.tramo-90      { background: #fed7d7; color: #9b2c2c; }
.tramo-120     { background: #fc8181; color: #fff; }
.tramo-vencido { background: #c53030; color: #fff; }

/* Colores modo oscuro para los tramos (mejor contraste) */
.dark-theme .tramo-ok      { background: #276749; color: #c6f6d5; }
.dark-theme .tramo-30      { background: #744210; color: #fefcbf; }
.dark-theme .tramo-60      { background: #7b341e; color: #feebc8; }
.dark-theme .tramo-90      { background: #9b2c2c; color: #fed7d7; }
.dark-theme .tramo-120     { background: #c53030; color: #fff; }
.dark-theme .tramo-vencido { background: #822727; color: #fff; }

.cartera-monto {
  font-weight: 700;
  font-size: 0.88rem;
  color: #2d3748;
}
.dark-theme .cartera-monto {
  color: #e2e8f0;
}

/* ── Cartera por pagar — diferenciador visual ───────────────────────── */
.cartera-pagar-icon {
  color: #e53e3e;
}
.cartera-pagar-total .cartera-header-valor {
  color: #e53e3e;
}
.dark-theme .cartera-pagar-icon {
  color: #fc8181;
}
.dark-theme .cartera-pagar-total .cartera-header-valor {
  color: #fc8181;
}

/* ── Fila resumen "Vencido" ─────────────────────────────────────────── */
.tramo-vencido-total {
  background: #9b2c2c;
  color: #fff;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.78rem;
}
.dark-theme .tramo-vencido-total {
  background: #822727;
  color: #fff;
}
.cartera-monto-vencido {
  color: #c53030;
  font-weight: 700;
}
.dark-theme .cartera-monto-vencido {
  color: #fc8181;
}
</style>


